import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  PersonaSchema,
  BoundariesSchema,
  type Persona,
  type Boundaries,
  type FlowWithRelations,
} from '@audiq/shared';
import { api } from '../../lib/api';
import { PersonaPreviewCard } from '../../components/PersonaPreviewCard';

interface Props {
  flow: FlowWithRelations;
}

const PersonaFormSchema = z.object({
  name: z.string().min(1).max(120),
  language_default: z.string().min(2).max(8),
  persona: PersonaSchema,
  boundaries: BoundariesSchema,
});
type PersonaForm = z.infer<typeof PersonaFormSchema>;

export function PersonaTab({ flow }: Props) {
  const qc = useQueryClient();

  const form = useForm<PersonaForm>({
    resolver: zodResolver(PersonaFormSchema),
    defaultValues: {
      name: flow.name,
      language_default: flow.language_default,
      persona: flow.persona,
      boundaries: flow.boundaries,
    },
  });

  // Quando il flow cambia (es. dopo update), reset del form coi nuovi default
  useEffect(() => {
    form.reset({
      name: flow.name,
      language_default: flow.language_default,
      persona: flow.persona,
      boundaries: flow.boundaries,
    });
  }, [flow.id, form]);

  const persona = form.watch('persona');
  const flowName = form.watch('name');
  const boundaries: Boundaries = form.watch('boundaries');

  const updateMut = useMutation({
    mutationFn: (data: PersonaForm) => api.updateFlow(flow.id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  function addBoundary() {
    form.setValue('boundaries', [...boundaries, ''], { shouldDirty: true });
  }
  function removeBoundary(idx: number) {
    form.setValue(
      'boundaries',
      boundaries.filter((_, i) => i !== idx),
      { shouldDirty: true },
    );
  }
  function setBoundary(idx: number, val: string) {
    const next = [...boundaries];
    next[idx] = val;
    form.setValue('boundaries', next, { shouldDirty: true });
  }

  return (
    <form
      onSubmit={form.handleSubmit((d) => updateMut.mutate(d))}
      className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]"
    >
      <div className="space-y-5">
        <div
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
            Identità
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Nome flow</label>
              <input
                type="text"
                {...form.register('name')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Lingua default</label>
              <select
                {...form.register('language_default')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              >
                <option value="it">Italiano</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="es">Español</option>
                <option value="ar">العربية</option>
              </select>
            </div>
          </div>
        </div>

        <div
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
            Persona
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Nome bot</label>
              <input
                type="text"
                {...form.register('persona.name')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Ruolo</label>
              <input
                type="text"
                {...form.register('persona.role')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Tono</label>
              <select
                {...form.register('persona.tone')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              >
                <option value="caldo">Caldo</option>
                <option value="professionale">Professionale</option>
                <option value="rassicurante">Rassicurante</option>
                <option value="neutro">Neutro</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Formality</label>
              <select
                {...form.register('persona.formality')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              >
                <option value="auto">Auto</option>
                <option value="tu">Tu</option>
                <option value="lei">Lei</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Emoji</label>
              <select
                {...form.register('persona.use_emoji')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              >
                <option value="mai">Mai</option>
                <option value="raro">Raro</option>
                <option value="moderato">Moderato</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">Apertura</label>
              <textarea
                {...form.register('persona.opening_message')}
                rows={2}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">Chiusura</label>
              <textarea
                {...form.register('persona.closing_message')}
                rows={2}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
          </div>
        </div>

        <div
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="mono" style={{ color: 'var(--mute)' }}>
              Boundaries{' '}
              <span style={{ color: 'var(--mute)', textTransform: 'none', letterSpacing: 0 }}>
                — regole rigide del bot, max 20
              </span>
            </h3>
            <button
              type="button"
              onClick={addBoundary}
              className="text-sm hover:underline"
              style={{ color: 'var(--primary-2)' }}
              disabled={boundaries.length >= 20}
            >
              + Aggiungi
            </button>
          </div>
          {boundaries.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--mute)' }}>
              Nessuna regola. Esempi: "Non dare diagnosi mediche", "Non promettere tempi di attesa".
            </p>
          )}
          <div className="space-y-2">
            {boundaries.map((b, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={b}
                  onChange={(e) => setBoundary(idx, e.target.value)}
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                  maxLength={200}
                />
                <button
                  type="button"
                  onClick={() => removeBoundary(idx)}
                  className="px-2 text-sm"
                  style={{ color: 'var(--danger)' }}
                  aria-label="Rimuovi"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <aside className="space-y-4">
        <PersonaPreviewCard persona={persona} flowName={flowName} />
        <button
          type="submit"
          disabled={!form.formState.isDirty || updateMut.isPending}
          className="w-full rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-50"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          {updateMut.isPending ? 'Salvataggio…' : 'Salva modifiche'}
        </button>
        {flow.is_active && form.formState.isDirty && (
          <p className="text-xs" style={{ color: 'var(--mute)' }}>
            Le modifiche richiedono una nuova pubblicazione per essere effettive in
            /api/flow/active.
          </p>
        )}
        {updateMut.isError && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            Errore di salvataggio
          </p>
        )}
      </aside>
    </form>
  );
}
